<template>
  <div>
    <article class="article">
      <header class="article-head">
        <div class="container">
          <div class="head-inner">
            <breadcrumbs :items="breadcrumbs"/>
            <p class="category">{{ article.category }}</p>
            <h1>{{ article.h1 }}</h1>
            <p class="byline">
              Par <router-link :to="site.author.path">{{ site.author.name }}</router-link>.
              Publié le <time :datetime="article.published">{{ formatDate(article.published) }}</time><template v-if="article.updated !== article.published">,
              mis à jour le <time :datetime="article.updated">{{ formatDate(article.updated) }}</time></template>.
              {{ article.readingMinutes }} min de lecture.
            </p>
            <p class="lead serif" @click="onLeadClick" v-html="lead"></p>
          </div>
        </div>
      </header>

      <div class="container article-layout">
        <nav class="toc" aria-label="Sommaire">
          <p class="toc-title">Dans ce guide</p>
          <ol>
            <li v-for="section in article.sections" :key="section.id">
              <a :href="'#' + section.id" @click.prevent="goTo(section.id)">{{ section.h2 }}</a>
            </li>
            <li v-if="article.faq && article.faq.length"><a href="#questions" @click.prevent="goTo('questions')">Questions fréquentes</a></li>
            <li v-if="article.sources && article.sources.length"><a href="#sources" @click.prevent="goTo('sources')">Sources</a></li>
          </ol>
        </nav>

        <div class="body prose prose--serif">
          <section v-for="section in article.sections" :key="section.id" class="section">
            <h2 :id="section.id">{{ section.h2 }}</h2>
            <content-blocks :blocks="section.blocks"/>
          </section>

          <section v-if="article.faq && article.faq.length" class="section faq-block">
            <h2 id="questions">Questions fréquentes</h2>
            <faq-section :items="article.faq"/>
          </section>

          <section v-if="article.sources && article.sources.length" class="section sources">
            <h2 id="sources">Sources</h2>
            <ul>
              <li v-for="source in article.sources" :key="source.url">
                <a :href="source.url" rel="noopener" target="_blank">{{ source.label }}</a>
              </li>
            </ul>
          </section>

          <aside class="disclaimer callout">
            <p>
              Ce guide est une information générale, à jour à la date indiquée. Il ne remplace pas l'avis de votre expert-comptable ou d'un juriste.
              Un point à corriger ? <router-link to="/contact">Écrivez-nous</router-link>.
            </p>
          </aside>
        </div>
      </div>
    </article>

    <section v-if="related.length" class="band band--white" aria-labelledby="related-title">
      <div class="container">
        <h2 id="related-title">À lire ensuite</h2>
        <article-list :articles="related"/>
      </div>
    </section>

    <call-to-action-component/>
  </div>
</template>

<script>
  import site from "@/content/site";
  import articles from "@/content/articles";
  import {inline} from "@/seo/inline";
  import {followInternalLink} from "@/seo/internalLinks";
  import {useSeo} from "@/seo/useSeo";
  import {organization, person, website, webPage, breadcrumb, blogPosting, faqPage, absoluteUrl} from "@/seo/schema";
  import Breadcrumbs from "@/components/Breadcrumbs.vue";
  import ContentBlocks from "@/components/ContentBlocks.vue";
  import FaqSection from "@/components/FaqSection.vue";
  import ArticleList from "@/components/ArticleList.vue";
  import CallToActionComponent from "@/components/CallToActionComponent.vue";

  /**
   * Un article du blog. Le contenu vient de src/content/articles (prop slug, fournie par le routeur).
   * La page expose un BlogPosting, un fil d'Ariane et, si l'article a une FAQ, un FAQPage.
   */
  export default {
    name: 'ArticleView',
    components: {Breadcrumbs, ContentBlocks, FaqSection, ArticleList, CallToActionComponent},
    props: {
      slug: {type: String, required: true},
    },
    setup(props) {
      const article = articles.find(entry => entry.slug === props.slug);
      const url = absoluteUrl(article.path);
      const graph = [
        organization(),
        person(),
        website(),
        webPage(article.path, article.title, article.description, {
          breadcrumb: {'@id': `${url}#breadcrumb`},
          mainEntity: {'@id': `${url}#article`},
          datePublished: article.published,
          dateModified: article.updated || article.published
        }),
        {...breadcrumb([
          {name: 'Accueil', path: '/'},
          {name: 'Blog', path: '/blog'},
          {name: article.title, path: article.path}
        ]), '@id': `${url}#breadcrumb`},
        blogPosting(article, `/img/og/blog-${article.slug}.jpg`)
      ];
      if (article.faq && article.faq.length) {
        graph.push(faqPage(article.faq));
      }
      useSeo({
        path: article.path,
        title: article.title,
        description: article.description,
        image: `/img/og/blog-${article.slug}.jpg`,
        type: 'article',
        published: article.published,
        modified: article.updated || article.published,
        graph
      });
    },
    data: function () {
      const article = articles.find(entry => entry.slug === this.slug);
      return {
        site,
        article,
        lead: inline(article.lead),
        breadcrumbs: [
          {name: 'Accueil', path: '/'},
          {name: 'Blog', path: '/blog'},
          {name: article.title, path: article.path}
        ],
        related: (article.related || []).map(slug => articles.find(entry => entry.slug === slug)).filter(Boolean),
      }
    },
    methods: {
      formatDate(iso) {
        return new Date(iso + 'T12:00:00Z').toLocaleDateString('fr-FR', {day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC'});
      },
      onLeadClick(event) {
        followInternalLink(event, this.$router);
      },
      goTo(id) {
        const target = document.getElementById(id);
        if (!target) return;
        target.scrollIntoView();
        target.setAttribute('tabindex', '-1');
        target.focus({preventScroll: true});
        history.replaceState(history.state, '', '#' + id);
      },
    }
  }
</script>

<style lang="stylus" scoped>
@import '~/src/common/style/constants.styl';

.article-head
  padding-top clamp(24px, 4vw, 48px)
  padding-bottom clamp(24px, 4vw, 40px)
  border-bottom 1px solid RULE
  background-color WHITE
  background-image repeating-linear-gradient(to bottom, transparent 0, transparent 35px, rgba(203, 216, 197, 0.4) 35px, rgba(203, 216, 197, 0.4) 36px)
  h1
    margin-top 14px
    font-size clamp(2rem, 4.2vw, 2.7rem)
  .category
    margin 22px 0 0
    color INK_SOFT
    font-weight 650
    font-size 0.95rem
  .byline
    margin 18px 0 0
    color MUTED
    font-size 0.95rem
  .lead
    margin-top 22px
    max-width 62ch

// Le titre s'aligne sur la colonne de texte : sommaire (230px) + espace (56px)
.head-inner
  max-width 720px

.serif
  font-family 'Iowan Old Style', 'Palatino Linotype', Palatino, 'Book Antiqua', Georgia, serif
  line-height 1.6

.article-layout
  display grid
  gap 32px
  padding-top 40px
  padding-bottom clamp(48px, 8vw, 96px)

.toc
  font-size 0.95rem
  .toc-title
    margin 0 0 10px
    font-weight 700
  ol
    margin 0
    padding 0
    list-style none
    border-left 3px solid LEMON
  li
    margin 0
    padding 6px 0 6px 14px
  a
    color INK_SOFT
    text-decoration none
    &:hover
      text-decoration underline

.body
  min-width 0
  max-width 720px
  .section + .section
    margin-top 2.4em
  // Le premier h2 n'a pas besoin du filet de séparation
  .section:first-child h2
    margin-top 0
    padding-top 0
    border-top 0
  .section h2
    margin-top 0
    margin-bottom 0.7em
  // La FAQ a déjà son propre espace sous le titre (h2 + .faq)
  .faq-block h2
    margin-bottom 0
  .sources ul
    padding-left 1.2em
    li
      margin-top 0.5em
      font-family 'Segoe UI', system-ui, sans-serif
      font-size 0.97rem
  .callout
    margin-top 2.4em

@media (min-width: 1000px)
  .head-inner
    margin-left 286px
  .article-layout
    grid-template-columns 230px minmax(0, 1fr)
    gap 56px
  .toc
    position sticky
    top 96px
    align-self start
</style>
